import tankUtils from "./tanks.utils";
import marpolDateUtils from './date.utils';
import moment from "moment";
import versions from "../ballast/versions";
import { EBallastOperationCodes, EBallastRecordCode, TBallastPlace } from "../ballast/@types";

// Previous implementation
// https://assist.wayship.io/app/vessel/onboard/48192a85999e7bd4372013f51507fc2b/marpol/list/undefined/wayship/marpolORB2::aa9dd6ca-e8cb-456b-82f5-b90dfcfd6d7f/RECEIPT_ORB2H2_2024-05-31.pdf?rev=10-eff2aabaa8257b638ed43a69b39be2fe

// New implementation
// https://console.wayship.io/api/attachments?docId=b9d7d10fb7d6ffd059574f79c63c50fc&attachmentName=Capture2.PNG&type=image/png

export function getFormattedDateTime(
    date: string,
    time: string,
    version: string
) {
    if (version === versions[0].value) {
        return time;
    } else {
        return `${moment(date, 'YYYY-MM-DD').format('DD-MMM-YYYY')} ${time}`;
    }
}

export function getItemNumber(
    recordCode: EBallastRecordCode,
    isBWTPUsed: boolean,
    operationType: EBallastOperationCodes
) {
    if (!recordCode) return '--';
    switch (recordCode) {
        case EBallastRecordCode.BALLAST:
            if (operationType === EBallastOperationCodes.BALLAST_MEPC80) {
                return 'A';
            }
            return isBWTPUsed ? '3.1/3.2' : 3.1;
        case EBallastRecordCode.DEBALLAST:
            const listOfDischargeCodes = [
                EBallastOperationCodes.DEBALLAST_IN_PORT_MEPC80,
                EBallastOperationCodes.DEBALLAST_IN_SEA_MEPC80,
            ];
            if (listOfDischargeCodes.includes(operationType)) {
                return 'B';
            } else if (
                operationType ===
                EBallastOperationCodes.DEBALLAST_TO_RECEPTION_FACILITY_MEPC80
            ) {
                return 'D';
            } else {
                return isBWTPUsed ? '3.3/3.2' : '3.3/3.1';
            }
        case EBallastRecordCode.EXCHANGE:
            const listOfExchangeCodes = [
                EBallastOperationCodes.EXCHANGE_BALLAST_USING_FLOW_THROUGH_MEPC80,
                EBallastOperationCodes.EXCHANGE_BALLAST_USING_FLOW_THROUGH_MEPC80,
                EBallastOperationCodes.EXCHANGE_INTERNAL_CIRCULATION_MEPC80,
            ];
            if (listOfExchangeCodes.includes(operationType)) {
                return 'C';
            }
            return 3.2;
        case EBallastRecordCode.INTERNAL:
            if (
                operationType ===
                EBallastOperationCodes.EXCHANGE_INTERNAL_CIRCULATION_MEPC80
            ) {
                return 'C';
            } else if (
                operationType ===
                EBallastOperationCodes.INTERNAL_TRANSFER_BETWEEN_BALLAST_MEPC80
            ) {
                return 'H';
            } else {
                return '3.2.2';
            }
        case EBallastRecordCode.EXCEPTIONAL:
            if (
                operationType ===
                EBallastOperationCodes.ACCIDENTAL_DISCHARGE_OF_BALLAST_WATER_MEPC80
            ) {
                return 'E';
            }
            return 3.5;
        case EBallastRecordCode.FAILURE:
            return 'F';
        case EBallastRecordCode.CLEANING:
            return 'G';
        case EBallastRecordCode.ADDITIONAL:
            if (
                operationType ===
                    EBallastOperationCodes.ADDITIONAL_PROCEDURES_MEPC80 ||
                operationType ===
                    EBallastOperationCodes.ADDITIONAL_PROCEDURES_MISSED_ENTRY_MEPC80
            ) {
                return 'H';
            }
            return 3.6;
    }
}

export function getPlaceOfBallast(placeOfBallast: TBallastPlace) {
    if (!placeOfBallast) return '--';
    switch (placeOfBallast.place) {
        case 'IN_PORT':
            return placeOfBallast.portName;
        case 'AT_SEA':
            return `${placeOfBallast.startPosition.latitude}, ${placeOfBallast.startPosition.longitude}`;
        case 'AT_RECEPTION_FACILITY':
            return placeOfBallast.portName ? placeOfBallast.portName : '--';
        default:
            return '--';
    }
}

export function getPlaceOfBallastEnd(placeOfBallast: TBallastPlace) {
    if (!placeOfBallast) return '--';
    switch (placeOfBallast.place) {
        case 'IN_PORT':
            return placeOfBallast.portName;
        case 'AT_SEA':
            return `${placeOfBallast.endPosition.latitude}, ${placeOfBallast.endPosition.longitude}`;
        case 'AT_RECEPTION_FACILITY':
            return placeOfBallast.portName ? placeOfBallast.portName : '--';
        default:
            return '--';
    }
}

export function getSignature(submittedBy: any, approvedBy: any) {
    if (approvedBy)
        return `SUBMITTED BY: ${submittedBy.name} [${
            submittedBy.designation
        }] - ${submittedBy.staffId} on ${moment(
            submittedBy.date
        ).toISOString()} ,APPROVED BY: ${approvedBy.name} [${
            approvedBy.designation
        }] - ${approvedBy.staffId} on ${moment(approvedBy.date).toISOString()}`;
    return `SUBMITTED BY: ${submittedBy.name} [${submittedBy.designation}] - ${
        submittedBy.staffId
    } on ${moment(submittedBy.date).toISOString()}`;
}

const marpolUtils = {
	...tankUtils,
	...marpolDateUtils,
	getAttachmentURL(fileName: string, id: string, _rev: string) {
		const fileExtension = (fileName.split('.').pop() || '').toLowerCase();
		let fileType = '';

		switch (fileExtension) {
		case 'png':
			fileType = 'image/png';
			break;
		case 'jpg':
		case 'jpeg':
			fileType = 'image/jpeg';
			break;
		case 'gif':
			fileType = 'image/gif';
			break;
		case 'pdf':
			fileType = 'application/pdf';
			break;
		case 'doc':
			fileType = 'application/msword';
			break;
		case 'docx':
			fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
			break;
		case 'xls':
			fileType = 'application/vnd.ms-excel';
			break;
		case 'xlsx':
			fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
			break;
		case 'ppt':
			fileType = 'application/vnd.ms-powerpoint';
			break;
		case 'pptx':
			fileType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
			break;
		// Add more cases as needed
		default:
			fileType = 'application/octet-stream'; // Default type if not matched
		}
		return `https://console.wayship.io/api/attachments?docId=${id}&attachmentName=${fileName}&type=${fileType}`;
	},
};
export default marpolUtils;
